import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { API_URL, buildMentionToken } from '../../lib/mentions';

function getMentionContext(text, cursor) {
  if (typeof cursor !== 'number') return null;
  const before = text.slice(0, cursor);
  const atIndex = before.lastIndexOf('@');
  if (atIndex < 0) return null;

  const leadingChar = atIndex === 0 ? ' ' : before[atIndex - 1];
  if (!/[\s([{\-–—,:;!?]/.test(leadingChar)) return null;

  const fragment = before.slice(atIndex + 1);
  if (fragment.includes('\n') || fragment.includes('\r') || fragment.includes('\u200b')) return null;
  if (fragment.includes('@')) return null;

  return {
    start: atIndex,
    end: cursor,
    query: fragment.trimStart()
  };
}

export function MentionTextarea({
  campaignId,
  language = 'fr',
  value = '',
  onChange,
  className,
  onKeyDown,
  ...props
}) {
  const textareaRef = useRef(null);
  const [mentionContext, setMentionContext] = useState(null);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const flattenedItems = useMemo(
    () => groups.flatMap((group) => group.items.map((item) => ({ ...item, groupType: group.type }))),
    [groups]
  );

  const refreshMentionContext = () => {
    const element = textareaRef.current;
    if (!element) return;
    const ctx = getMentionContext(element.value, element.selectionStart);
    setMentionContext(ctx);
    if (!ctx) {
      setOpen(false);
      setGroups([]);
      setActiveIndex(0);
    }
  };

  useEffect(() => {
    if (!campaignId || !mentionContext) return;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/campaigns/${campaignId}/mentions/search`, {
          params: { q: mentionContext.query || '' }
        });
        if (cancelled) return;
        const nextGroups = response.data?.groups || [];
        setGroups(nextGroups);
        setOpen(nextGroups.length > 0);
        setActiveIndex(0);
      } catch (error) {
        if (!cancelled) {
          setGroups([]);
          setOpen(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [campaignId, mentionContext]);

  const emitChange = (nextValue) => {
    if (!onChange) return;
    onChange({
      target: { value: nextValue },
      currentTarget: { value: nextValue }
    });
  };

  const insertMention = (item) => {
    if (!textareaRef.current || !mentionContext) return;
    const token = buildMentionToken(item, language);
    const currentValue = textareaRef.current.value;
    const nextValue = `${currentValue.slice(0, mentionContext.start)}${token}${currentValue.slice(mentionContext.end)}`;
    const nextCursor = mentionContext.start + token.length;

    emitChange(nextValue);
    setOpen(false);
    setGroups([]);
    setMentionContext(null);
    setActiveIndex(0);

    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.selectionStart = nextCursor;
      textareaRef.current.selectionEnd = nextCursor;
    });
  };

  const handleKeyDown = (event) => {
    if (open && flattenedItems.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % flattenedItems.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + flattenedItems.length) % flattenedItems.length);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        insertMention(flattenedItems[activeIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => {
          if (onChange) onChange(event);
          requestAnimationFrame(refreshMentionContext);
        }}
        onClick={refreshMentionContext}
        onKeyUp={refreshMentionContext}
        onKeyDown={handleKeyDown}
        className={className}
        {...props}
      />
      {open && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
          {loading && (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              {language === 'fr' ? 'Recherche...' : 'Searching...'}
            </div>
          )}
          {!loading && groups.length === 0 && (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              {language === 'fr' ? 'Aucun resultat' : 'No results'}
            </div>
          )}
          {!loading &&
            groups.map((group) => (
              <div key={group.type} className="mb-1 last:mb-0">
                <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {language === 'fr' ? group.label_fr : group.label_en}
                </div>
                {group.items.map((item) => {
                  const itemIndex = flattenedItems.findIndex((candidate) => candidate.id === item.id && candidate.type === item.type);
                  const selected = itemIndex === activeIndex;
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      type="button"
                      className={cn(
                        'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
                        selected && 'bg-accent'
                      )}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => insertMention(item)}
                    >
                      <span className="truncate">
                        {item.name}
                      </span>
                      <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                        {language === 'fr' ? item.type_label_fr : item.type_label_en}
                        {(language === 'fr' ? item.status_label_fr : item.status_label_en)
                          ? ` • ${language === 'fr' ? item.status_label_fr : item.status_label_en}`
                          : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

